import { Component, ViewChild, ElementRef, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { DocumentMethods } from "@methods/DocumentMethods";
import { ToastrService } from "ngx-toastr";
import { DevManagementDataService } from "@app/admin/services/devmanagement.service";
import { KeyValue } from "@models/generic/KeyValuePair";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { InputTooltipDirective } from "@app/shared/directives/input-tooltip.directive";
import { firstValueFrom } from "rxjs";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/admin/i18n/scope";

@Component({
    selector: "dev-import",
    templateUrl: "./dev-import.component.html",
    styleUrls: ["./dev-import.component.scss"],
    imports: [FormsModule, NgSelectModule, InputTooltipDirective, TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class DevImportComponent {
    private dataService = inject(DevManagementDataService);
    private promptService = inject(CerrixPromptService);
    private _toastr = inject(ToastrService);
    private translation = injectTranslation(scope);

    @ViewChild("fileInput") fileInput: ElementRef;

    allowedFileExtensions =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.xls,.xlsx";

    importTypes: KeyValue<number, string>[];
    selectedImportType: number;

    fileValid = false;
    selectedFile: File;
    selectedFileName = "";

    importValidations: KeyValue<string, boolean>[];

    constructor() {
        this.dataService.getImportTypes().subscribe((x) => {
            this.importTypes = x;
            this.selectedImportType = x[0].Key;
        });
    }

    async import() {
        if (this.selectedImportType > 0 && this.fileValid) {
            await this.translation.ready();
            const prompt = this.promptService.loader(this.translation.t("devImport.importingPleaseWait"));

            try {
                this.importValidations = await firstValueFrom(this.dataService
                    .import(this.selectedImportType, this.selectedFile));
            } finally {
                prompt.close();
            }
        }
    }

    async updateFile() {
        this.fileValid = false;
        this.selectedFile = null;
        this.selectedFileName = "";

        const allowedFiles = DocumentMethods.filterAllowedFiles(
            this._toastr,
            this.fileInput.nativeElement.files,
            this.allowedFileExtensions
        );

        if (allowedFiles.length !== 1) {
            if (this.fileInput.nativeElement.files.length > 0) {
                await this.translation.ready();
                this._toastr.warning(this.translation.t("devImport.filesNotValid"));
            }

            return;
        }

        this.selectedFile = allowedFiles[0];
        this.selectedFileName = this.selectedFile.name;
        this.fileValid = true;
    }
}
